import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import {
  ColorValues,
  Colors,
  Flex,
  IconShare,
  IconWonFilled,
  Typography,
} from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import ReAnimated, {
  useAnimatedProps,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { renderHTMLToText } from 'shared/src/utils/parseHTMLToReactNative/parseHTMLToReactNative';
import { isAndroid } from 'shared/src/utils';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';
import { ScrollContext } from '../../context/ScrollContext';

const ICON_SIZE = 18;
const ANIMATION_DURATION = 200;
const TOGGLE_INTERVAL = 1200;

interface AffiliateFloatingButtonProps {
  text: string;
  marginBottom: number;
  onPress: () => void;
  onView: () => void;
}

export const AffiliateFloatingButton = ({
  text,
  marginBottom,
  onPress,
  onView,
}: AffiliateFloatingButtonProps) => {
  const { scrollY } = useContext(ScrollContext);

  const rewardOpacity = useSharedValue(0);
  const shareOpacity = useSharedValue(1);
  const isLogViewRef = useRef(false);
  const showReward = useRef(true);

  const handleView = useCallback(() => {
    if (!isLogViewRef.current) {
      isLogViewRef.current = true;
      onView();
    }
  }, [onView]);

  const animatedStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    bottom: marginBottom + 16 + 32,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    opacity: scrollY
      ? isAndroid
        ? scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
          ? 1
          : 0
        : withTiming(
            scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE ? 1 : 0,
            {
              duration: SCROLL_ANIMATION_DURATION,
            },
          )
      : 0,
  }));

  const animatedProps = useAnimatedProps(() => ({
    pointerEvents:
      scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
        ? ('auto' as const)
        : ('none' as const),
  }));

  const shareAnimatedStyle = useAnimatedStyle(() => ({
    opacity: shareOpacity.value,
  }));

  const rewardAnimatedStyle = useAnimatedStyle(() => ({
    opacity: rewardOpacity.value,
  }));

  useAnimatedReaction(
    () => scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
    (isVisible, prevIsVisible) => {
      if (isVisible && !prevIsVisible) {
        scheduleOnRN(handleView);
      }
    },
  );

  useEffect(() => {
    const interval = setInterval(() => {
      showReward.current = !showReward.current;
      const nextShowReward = showReward.current;

      rewardOpacity.value = withTiming(nextShowReward ? 1 : 0, {
        duration: ANIMATION_DURATION,
      });
      shareOpacity.value = withTiming(nextShowReward ? 0 : 1, {
        duration: ANIMATION_DURATION,
      });
    }, TOGGLE_INTERVAL);

    return () => clearInterval(interval);
  }, [rewardOpacity, shareOpacity]);

  return (
    <ReAnimated.View style={animatedStyle} animatedProps={animatedProps}>
      <Flex.Row
        onPress={onPress}
        alignItems={'center'}
        gap={4}
        padding={{ horizontal: 14, vertical: 10 }}
        backgroundColorValue={ColorValues.white}
        borderRadius={50}
        shadowStep={'large'}
      >
        <Flex width={ICON_SIZE} height={ICON_SIZE}>
          <ReAnimated.View
            style={[StyleSheet.absoluteFill, styles.iconCenter, shareAnimatedStyle]}
          >
            <IconShare size={ICON_SIZE} fill={Colors.content_secondary} />
          </ReAnimated.View>
          <ReAnimated.View
            style={[StyleSheet.absoluteFill, styles.iconCenter, rewardAnimatedStyle]}
          >
            <IconWonFilled size={ICON_SIZE} fill={ColorValues.orange30} />
          </ReAnimated.View>
        </Flex>
        <Typography type="subtitle3" color="content_secondary">
          {renderHTMLToText(text)}
        </Typography>
      </Flex.Row>
    </ReAnimated.View>
  );
};

const styles = StyleSheet.create({
  iconCenter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
